import React, { useState, useCallback, useEffect } from "react"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Icon from "aldoo-ra/Icon"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import { HistoryAPI } from "../history-api"
import HistoryDetailsPanel from "./HistoryDetailsPanel"
import HistoryEntryCard from "./HistoryEntryCard"
import { DocumentTextIcon } from "@heroicons/react/24/outline"

/**
 * History Tab - DataTable with full search and filter capabilities
 */
const HistoryTab = () => {
  const [filters, setFilters] = useState({
    targetModel: [],
    action: [],
    user: [],
    tags: [],
    search: ""
  })
  const [tableRefreshKey, setTableRefreshKey] = useState(0)
  const [availableModels, setAvailableModels] = useState([])
  const [availableTags, setAvailableTags] = useState([])
  const [availableUsers, setAvailableUsers] = useState([])
  const [selectedEntry, setSelectedEntry] = useState(null)

  // Load available models from statistics
  useEffect(() => {
    const loadModels = async () => {
      const response = await HistoryAPI.GetStatistics()
      if (response.result && response.statistics.byModel) {
        const models = response.statistics.byModel.map(m => ({
          value: m.model,
          name: m.model
        }))
        setAvailableModels(models)
      }
    }
    loadModels()
  }, [])

  // Load available tags
  useEffect(() => {
    const loadTags = async () => {
      const response = await HistoryAPI.GetTags()
      if (response.result && response.tags) {
        const tags = response.tags.map(tag => ({
          value: tag,
          name: tag
        }))
        setAvailableTags(tags)
      }
    }
    loadTags()
  }, [])

  // Load available users
  useEffect(() => {
    const loadUsers = async () => {
      const response = await HistoryAPI.GetUsers()
      if (response.result && response.users) {
        const users = response.users.map(u => ({
          value: u.userId,
          name: u.author,
          email: u.email
        }))
        setAvailableUsers(users)
      }
    }
    loadUsers()
  }, [])

  // API method for DataTable
  const fetchHistory = useCallback(async (requestFields = {}) => {
    try {
      const {
        page = 1,
        itemsPerPage = 25,
        match = {},
        search: searchObject = {}
      } = requestFields

      // Extract search string from DataTable's search object
      // DataTable creates MongoDB-style search objects like: { $or: [{ field1: "term" }, { field2: "term" }] }
      // or simple field search like: { field: "term" }
      let searchString = null

      if (searchObject && typeof searchObject === 'object' && Object.keys(searchObject).length > 0) {
        // If it's a $or search, extract the search term from first field
        if (searchObject.$or && Array.isArray(searchObject.$or) && searchObject.$or.length > 0) {
          const firstField = Object.keys(searchObject.$or[0])[0]
          searchString = searchObject.$or[0][firstField]
        } else {
          // Simple field search
          const firstField = Object.keys(searchObject)[0]
          searchString = searchObject[firstField]
        }
      }

      // Read current filter values directly from state at call time
      // This ensures we always get the latest filter values
      const targetModel = filters.targetModel.length > 0 ? filters.targetModel[0].value : null
      const action = filters.action.length > 0 ? filters.action[0].value : null
      const tags = filters.tags.length > 0 ? filters.tags.map(t => t.value) : []
      const user = filters.user.length > 0 ? filters.user[0].value : null

      console.log("Fetching history with filters:", {
        targetModel,
        action,
        tags,
        user,
        searchString,
        filters: filters
      })

      const response = await HistoryAPI.ListHistory({
        page,
        itemsPerPage,
        targetModel,
        action,
        tags,
        user,
        search: searchString,
        ...match
      })

      return {
        result: response.result,
        data: response.data || [],
        totalItems: response.totalItems || 0
      }
    } catch (error) {
      console.error("Failed to fetch history:", error)
      return {
        result: false,
        data: [],
        totalItems: 0
      }
    }
  }, [filters])

  // Apply filters - triggers re-fetch via useCallback dependency
  const handleApplyFilters = () => {
    // Increment refresh key to trigger DataTable re-fetch
    // The fetchHistory callback will read the current filter state
    setTableRefreshKey(prev => prev + 1)
  }

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      targetModel: [],
      action: [],
      user: [],
      tags: [],
      search: ""
    })
    setTableRefreshKey(prev => prev + 1)
  }

  // Get action badge variant
  const getActionVariant = (action) => {
    switch (action) {
      case 'create':
        return "admin_success"
      case 'update':
        return "admin_info"
      case 'delete':
        return "admin_error"
      case 'soft-delete':
        return "admin_warning"
      default:
        return "admin"
    }
  }

  // Header controls
  const headerControls = (
    <div className="w-full flex items-end gap-3 mb-4">
      <div className="flex flex-col gap-1" style={{ width: "190px" }}>
        <Typography variant="admin_txt2_dim">
          Model
        </Typography>

        <DropdownSelect
          variant="admin"
          options={availableModels}
          value={filters.targetModel}
          onChange={(values) => {
            console.log("DropdownSelect onChange called with:", values)
            setFilters(prev => {
              const newFilters = { ...prev, targetModel: values || [] }
              console.log("Setting new filters:", newFilters)
              return newFilters
            })
            // Trigger table refresh immediately when filter changes
            setTimeout(() => setTableRefreshKey(prev => prev + 1), 0)
          }}
          placeholder="All Models"
        />
      </div>

      <div className="flex flex-col gap-1" style={{ width: "160px" }}>
        <Typography variant="admin_txt2_dim">
          Action
        </Typography>

        <DropdownSelect
          variant="admin"
          options={[
            { value: "create", name: "Create" },
            { value: "update", name: "Update" },
            { value: "delete", name: "Delete" },
            { value: "soft-delete", name: "Soft Delete" }
          ]}
          value={filters.action}
          onChange={(values) => {
            setFilters(prev => ({ ...prev, action: values || [] }))
            // Trigger table refresh immediately when filter changes
            setTimeout(() => setTableRefreshKey(prev => prev + 1), 0)
          }}
          placeholder="All Actions"
        />
      </div>

      <div className="flex flex-col gap-1" style={{ width: "190px" }}>
        <Typography variant="admin_txt2_dim">
          User
        </Typography>

        <DropdownSelect
          variant="admin"
          options={availableUsers}
          value={filters.user}
          onChange={(values) => {
            setFilters(prev => ({ ...prev, user: values || [] }))
            // Trigger table refresh immediately when filter changes
            setTimeout(() => setTableRefreshKey(prev => prev + 1), 0)
          }}
          placeholder="All Users"
          renderOption={(option) => (
            <div className="flex flex-col">
              <span className="text-admin_text font-medium">
                {option.name}
              </span>

              {option.email && (
                <span className="text-admin_text_secondary text-xs">
                  {option.email}
                </span>
              )}
            </div>
          )}
        />
      </div>

      <div className="flex flex-col gap-1" style={{ width: "190px" }}>
        <Typography variant="admin_txt2_dim">
          Tags
        </Typography>

        <DropdownSelect
          variant="admin"
          options={availableTags}
          value={filters.tags}
          onChange={(values) => {
            setFilters(prev => ({ ...prev, tags: values || [] }))
            // Trigger table refresh immediately when filter changes
            setTimeout(() => setTableRefreshKey(prev => prev + 1), 0)
          }}
          placeholder="All Tags"
          multiSelect={true}
        />
      </div>

      <Button
        variant="admin_icon"
        onClick={handleClearFilters}
        className="ml-auto"
      >
        <Icon 
          id="filter_remove" 
          className="h-8 w-8 text-admin_primary hover:text-admin_error transition-colors" 
          recolor 
        />
      </Button>
    </div>
  )

  return (
    <div className="bg-admin_canvas text-admin_text flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-admin_paper border-b border-admin_bordered p-4">
        <div className="flex flex-col">
          <Typography 
            component="h1"
            variant="admin_h4"
          >
            Change History
          </Typography>

          <Typography 
            variant="admin_body_dim" 
            className="my-0"
          >
            Browse and search through all content changes with advanced filters
          </Typography>
        </div>
      </div>

      {/* Content */}
      <div className="relative flex flex-col flex-1 p-6 z-20">
        <DataTable
          variant="admin_browser"
          apiMethod={fetchHistory}
          refreshKey={tableRefreshKey}
          requestFields={{}}
          headerControls={headerControls}
          searchField="changelog targetModel author"
          itemsPerPage={25}
          preferredHeight="100%"
          localUserPreferencesKey="admin.history.table"
          onRowClick={(entry) => setSelectedEntry(entry)}
          renderRow={(entry, index) => (
            <div key={entry._id || index} className="mb-2">
              <HistoryEntryCard
                entry={entry}
                isSelected={selectedEntry?._id === entry._id}
                onClick={() => setSelectedEntry(entry)}
              />
            </div>
          )}
          styleOverrides={{
            tableWrapper: "bg-admin_canvas rounded-lg p-2 overflow-y-auto",
            table: "w-full",
            tbody: "space-y-2",
            header: "w-full flex justify-between items-end gap-2 mb-4",
            container: "relative w-full flex flex-col h-full justify-start bg-admin_paper rounded-3xl px-4 pt-4 pb-6",
            searchInput: "absolute w-[350px] h-[30px] -top-[88px] -right-2 text-text bg-transparent outline-none flex-1 px-2 py-1",
          }}
          noData={
            <div className="text-center py-12">
              <DocumentTextIcon className="w-16 h-16 text-admin_text_secondary mx-auto mb-4" />
              
              <Typography 
                variant="admin_h4" 
                className="text-admin_text_secondary mb-2"
              >
                No history entries found
              </Typography>

              <Typography variant="admin_txt1_dim">
                Try adjusting your filters or search criteria
              </Typography>
            </div>
          }
        />
      </div>

      {/* Details Modal */}
      {selectedEntry && (
        <Modal
          isOpen={!!selectedEntry}
          onClose={() => setSelectedEntry(null)}
          title="Change Details"
          variant="admin_drawer_right"
          closeOnClickOutside={false}
          passInteractionEvents={true}
          styleOverrides={{
            header: "relative flex justify-between items-center px-6 py-6 border-b border-admin_bordered",
            closeButton: "absolute left-2 top-1/2 -translate-y-1/2 hover:text-admin_text z-10"
          }}
        >
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper">
            <HistoryDetailsPanel
              entry={selectedEntry}
              onClose={() => setSelectedEntry(null)}
            />
          </div>
        </Modal>
      )}
    </div>
  )
}

export default HistoryTab
